import {useRef} from 'react';
import styled from 'styled-components';
import {formatDateFull} from '../utils/formatDate';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

interface TicketAttachment {
	id: string;
	filename: string;
	contentType: string;
	sizeBytes: number;
	downloadUrl: string;
	createdAt: string;
	uploadedByName?: string | null;
}

interface Props {
	attachments: readonly TicketAttachment[];
	onUploadFile?: (file: File) => void;
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function fileExtension(filename: string): string {
	const dot = filename.lastIndexOf('.');
	if (dot === -1 || dot === filename.length - 1) return '';
	return filename.slice(dot + 1).toUpperCase();
}

// File-type icon palette, keyed by extension group. Ports the status/* and
// subtle token pairs the icon previously read via inline var(--fd-*) styles.
type IconVariant = 'pdf' | 'image' | 'sheet' | 'archive' | 'other';
const ICON_COLORS: Record<IconVariant, {bg: string; color: string}> = {
	pdf: {bg: '#fee2e2', color: '#991b1b'}, // status-critical
	image: {bg: '#dbeafe', color: '#1e40af'}, // status-open
	sheet: {bg: '#dcfce7', color: '#166534'}, // status-resolved
	archive: {bg: '#fef9c3', color: '#854d0e'}, // status-in-progress
	other: {bg: '#f1f5f9', color: '#6b7280'}, // bg-subtle / text-secondary
};

function iconVariantForExtension(ext: string): IconVariant {
	switch (ext) {
		case 'PDF':
			return 'pdf';
		case 'PNG':
		case 'JPG':
		case 'JPEG':
		case 'GIF':
		case 'SVG':
			return 'image';
		case 'CSV':
		case 'XLS':
		case 'XLSX':
			return 'sheet';
		case 'ZIP':
		case 'TAR':
		case 'GZ':
			return 'archive';
		default:
			return 'other';
	}
}

const FileIcon = styled.span<{$variant: IconVariant}>`
	display: inline-flex;
	height: 28px;
	width: 28px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.xs};
	font: ${p => p.theme.ticketing.font.body3};
	background: ${p => ICON_COLORS[p.$variant].bg};
	color: ${p => ICON_COLORS[p.$variant].color};
`;

function FileTypeIcon({ext}: {ext: string}) {
	const variant = iconVariantForExtension(ext);
	const label = ext.length > 4 ? ext.slice(0, 4) : ext || 'FILE';
	return (
		<FileIcon data-testid="attachment-file-icon" data-extension={ext || 'FILE'} aria-hidden="true" $variant={variant}>
			{label}
		</FileIcon>
	);
}

const HiddenInput = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const Header = styled.header`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Title = styled.h3`
	margin: 0;
	font: ${p => p.theme.ticketing.font.subheading1};
	letter-spacing: 0.15px;
	color: #0f172a;
`;

const PrimaryButton = styled.button`
	display: inline-flex;
	align-items: center;
	border: 0;
	border-radius: ${p => p.theme.ticketing.radius.xs};
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.label1};
	color: #ffffff;
	background: ${p => p.theme.ticketing.color.interactivePrimary};
	cursor: pointer;
	transition: opacity 0.15s;
	&:hover {
		opacity: 0.9;
	}
`;

const EmptyState = styled.div`
	margin-top: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	border-radius: 6px;
	border: 1px dashed #e2e8f0;
	padding: 48px 24px;
	text-align: center;
`;

const EmptyIcon = styled.svg`
	height: 40px;
	width: 40px;
	color: ${p => p.theme.ticketing.color.textSecondary};
`;

const EmptyText = styled.p`
	margin: 0;
	font: ${p => p.theme.ticketing.font.body2};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;

const TableCard = styled.div`
	margin-top: 16px;
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const HeadRow = styled.tr`
	background: #f8fafc;
`;

const Th = styled.th`
	padding: 10px 16px;
	text-align: left;
	font: ${p => p.theme.ticketing.font.overline};
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const SrOnly = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const Row = styled.tr`
	border-top: 1px solid #f1f5f9;
	&:hover {
		background: #f8fafc;
	}
`;

const Td = styled.td<{$right?: boolean}>`
	padding: 12px 16px;
	vertical-align: middle;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #475569;
	${p => p.$right && 'text-align: right;'}
`;

const NameCellInner = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
`;

const Filename = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #0f172a;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const DownloadLink = styled.a`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	text-decoration: none;
	&:hover {
		opacity: 0.8;
	}
`;

const MonoDate = styled.span`
	font: ${p => p.theme.ticketing.font.mono};
`;

export function TicketAttachmentsTab({attachments, onUploadFile}: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file && onUploadFile) {
			onUploadFile(file);
		}
		event.target.value = '';
	};

	const isEmpty = attachments.length === 0;

	return (
		<section data-testid="attachments-tab">
			<HiddenInput ref={fileInputRef} type="file" onChange={handleFileChange} data-testid="attachments-file-input" />

			<Header>
				<Title data-testid="attachments-count">Attachments</Title>
				{!isEmpty && (
					<PrimaryButton type="button" onClick={handleUploadClick} data-testid="attachments-upload-button">
						Upload file
					</PrimaryButton>
				)}
			</Header>

			{isEmpty ? (
				<EmptyState data-testid="attachments-empty-state">
					<EmptyIcon aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
						/>
					</EmptyIcon>
					<EmptyText>No files attached yet</EmptyText>
					<PrimaryButton type="button" onClick={handleUploadClick} data-testid="attachments-upload-button-empty">
						Upload file
					</PrimaryButton>
				</EmptyState>
			) : (
				<TableCard>
					<Table data-testid="attachments-table">
						<thead>
							<HeadRow>
								<Th scope="col">Name</Th>
								<Th scope="col">Size</Th>
								<Th scope="col">Uploaded by</Th>
								<Th scope="col">When</Th>
								<Th scope="col">
									<SrOnly>Actions</SrOnly>
								</Th>
							</HeadRow>
						</thead>
						<tbody>
							{attachments.map(attachment => {
								const ext = fileExtension(attachment.filename);
								return (
									<Row key={attachment.id} data-testid="attachment-row">
										<Td>
											<NameCellInner>
												<FileTypeIcon ext={ext} />
												<Filename>{attachment.filename}</Filename>
											</NameCellInner>
										</Td>
										<Td>{formatBytes(attachment.sizeBytes)}</Td>
										<Td>{attachment.uploadedByName ?? '—'}</Td>
										<Td>
											<MonoDate>{formatDateFull(attachment.createdAt)}</MonoDate>
										</Td>
										<Td $right>
											<DownloadLink
												href={ticketingAttachmentDownloadUrl(attachment.downloadUrl)}
												download={attachment.filename}
												data-testid="attachment-download-button"
											>
												Download
											</DownloadLink>
										</Td>
									</Row>
								);
							})}
						</tbody>
					</Table>
				</TableCard>
			)}
		</section>
	);
}
